import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
  Image,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb
} from '@chakra-ui/react';
import CloseModalIcon from '../../assets/close-modal-icon.svg';
import InvestIcon from '../../assets/invest-icon.svg';
import TotalInvestIcon from '../../assets/total-invest-icon.svg';
import CommunityIcon from '../../assets/community-icon.svg';
import InformGrayIcon from '../../assets/inform-gray-icon.svg';
import Button from '../Button';
import { TooltipComponent } from '../Tooltip';
import MyRankComponent from '../Header/HeaderNewsletter/MyRank';
import { useAtom } from 'jotai';
import { profileAtom } from '../Settings/atom';
import { useNavigate } from 'react-router-dom';
import { DataLayer } from '../../utils/data-layer';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

function InvestArticleModal(props) {
  const {
    isOpen,
    onClose,
    article,
    isInvesting,
    bgColorOverlay,
    setOpenLearnMorePanel,
    initialPoints,
    alreadyInvested,
    handleInvestArticle,
    handleFollowUser,
    isFollowingUser
  } = props;
  const [profile] = useAtom(profileAtom);
  const [points, setPoints] = useState(initialPoints || 10);
  const navigate = useNavigate();
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  const handleChangePoints = (value) => {
    if (alreadyInvested) return;
    const availablePoints = profile?.totalRawPointsRemaining || 0;

    if (value <= availablePoints) {
      setPoints(value);
    }
  };

  const handleInvestWithTracking = (pointsToInvest) => {
    // Track the investment event
    DataLayer.trackArticleInvest(article?.type, article?.id || '', pointsToInvest);

    // Call the original investment handler
    handleInvestArticle(pointsToInvest);
  };

  return (
    <Modal isOpen={isOpen} isCentered onClose={onClose}>
      <ModalOverlay backgroundColor={bgColorOverlay || 'rgba(0, 0, 0, 0.5)'} />
      <ModalContent
        px={'15px'}
        py={5}
        bgColor={'#FAFAFA'}
        rounded={'12px'}
        maxW={{ base: '360px', md: '396px' }}
      >
        <ModalHeader p={0} w={'100%'} m={0} maxWidth={'unset'}>
          <div className="flex w-full items-center justify-between pb-2 border-b border-earth-brown-50">
            <p className="text-lg font-[800]">Invest</p>
            <button className="w-10 h-10 flex items-center justify-center" onClick={onClose}>
              <Image className="cursor-pointer" src={CloseModalIcon} alt="Close" />
            </button>
          </div>
        </ModalHeader>
        <ModalBody py={6} px={0}>
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-col items-center gap-1">
              <Image className="w-10 h-10" src={InvestIcon} alt="Invest" />
              <p className="text-2xl font-[800] text-center text-growth-green-500">
                {alreadyInvested ? 'You have invested in this article!' : 'Invest in this article'}
              </p>
            </div>
            <div className="text-sm flex flex-col md:flex-row items-center font-[600]">
              Earn a share of the {article?.sharingPoolPercentage}% ad revenue pool{' '}
              <span
                className="text-growth-green-500 font-[900] ml-0 md:ml-4 cursor-pointer hover:opacity-80"
                onClick={() => setOpenLearnMorePanel(true)}
              >
                Learn more
              </span>
            </div>
            <div className="flex justify-between w-full mb-2">
              <div className="flex items-center gap-1">
                <img src={CommunityIcon} className="w-5 h-5" alt="Community" />
                <p className="text-sm font-[600] ml-1">
                  {article?.investmentData?.totalInvestors || 0}{' '}
                  {article?.investmentData?.totalInvestors === 1 ? 'Investor' : 'Investors'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <img src={TotalInvestIcon} className="w-5 h-5" alt="Community" />
                <p className="text-sm font-[600] ml-1">
                  {article?.investmentData?.totalInvestedPoints || 0} Seeds Invested
                </p>
              </div>
            </div>
            <div className="p-4 rounded-[8px] bg-white w-full flex flex-col gap-2">
              <p className="text-sm font-[800]">
                {alreadyInvested ? 'Seeds invested:' : 'Seeds to Invest'}
              </p>
              <Slider
                aria-label="slider"
                min={10}
                max={100}
                value={points}
                onChange={(val) => handleChangePoints(val)}
              >
                <SliderTrack h="6px" bg="anti-flash-white" borderRadius={'20px'}>
                  <SliderFilledTrack
                    borderRadius={'20px'}
                    h="6px"
                    bg="linear-gradient(90deg, #C8E6C9 1.02%, #4CAF50 55.95%, #204A22 91.59%)"
                  />
                </SliderTrack>
                {alreadyInvested || (
                  <SliderThumb border={'1px'} borderColor={'#204A22'} boxSize={4} />
                )}
              </Slider>
              <div className="flex justify-between items-center">
                <p className="text-sm text-02-dim-gray font-[600]">Min: 10</p>
                <p className="text-sm font-[600] text-growth-green-900">{points} Seeds</p>
                <div className="text-sm text-02-dim-gray flex gap-1 items-center font-[600]">
                  <TooltipComponent
                    maxW={'221px'}
                    description={
                      'The maximum Seeds to invest is limited to 100, regardless of your available balance.'
                    }
                    fontSize={'xs'}
                    color={'#204A22'}
                    placement={'top'}
                    distance={{ base: '80px', md: '-30px' }}
                    Icon={InformGrayIcon}
                  />
                  Max: 100
                </div>
              </div>
            </div>
            {alreadyInvested || (
              <div className="flex justify-between items-center w-full mt-4">
                <p className="text-sm font-[800]">Current Seeds Balance:</p>
                <MyRankComponent
                  pointName={'Seeds'}
                  height="h-8"
                  profile={profile}
                  pointsBalance={profile.totalRawPointsRemaining}
                  shouldShowMenu={false}
                />
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter w="100%" p="0" flexDir={'column'}>
          {article?.creator?.isFollowing || (
            <div className="mb-2 flex items-center gap-1">
              <img src={InformGrayIcon} className="w-5 h-5" alt="Inform" />
              <p className="text-sm text-02-dim-gray font-[600] ">
                Follow the writer first to invest in this article.
              </p>
            </div>
          )}

          {alreadyInvested ? (
            <div className="flex justify-between items-center gap-4 w-full">
              <Button variant="outline" onClick={onClose} className="w-full h-[42px] font-[900]">
                Check later
              </Button>
              <Button
                onClick={() => navigate('/article/manage?tab=investments')}
                className="w-full h-[42px] font-[900]"
              >
                View Earnings
              </Button>
            </div>
          ) : !article?.creator?.isFollowing ? (
            <div className="flex flex-col justify-between items-center gap-2 w-full">
              <Button
                onClick={() => {
                  if (!userId) return UnauthorizedDispatcher();
                  handleFollowUser();
                }}
                className="w-full h-[42px] font-[900]"
                isLoading={isFollowingUser}
              >
                Follow Writer
              </Button>
              <Button
                onClick={() => handleInvestWithTracking(points)}
                className="w-full h-[42px] font-[900]"
                isLoading={isInvesting}
                disabled={!article?.creator?.isFollowing}
              >
                Invest {points} Seeds
              </Button>
            </div>
          ) : (
            <TooltipComponent
              maxW={'300px'}
              description={
                profile?.totalRawPointsRemaining < 10
                  ? 'You earn Seeds when others give you Twigs'
                  : ''
              }
              fontSize={'xs'}
              color={'#204A22'}
              placement={'top'}
              distance={{ base: '80px', md: '-30px' }}
            >
              <Button
                onClick={() => handleInvestWithTracking(points)}
                className="w-full h-[42px] font-[900]"
                isLoading={isInvesting}
                disabled={!article?.creator?.isFollowing || profile?.totalRawPointsRemaining < 10}
              >
                {profile?.totalRawPointsRemaining < 10
                  ? 'Min 10 Seeds Required'
                  : `Invest ${points} Seeds`}
              </Button>
            </TooltipComponent>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default InvestArticleModal;
